import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { toast } from "sonner";
import {
  MessageCircle, Send, Search, Loader2, User as UserIcon, ExternalLink, Plus, Pencil, Trash2, ArrowLeft,
} from "lucide-react";

const STATUS_FILTERS = [
  { id: "", label: "Tutte" },
  { id: "unread", label: "Da leggere" },
  { id: "Aperta", label: "Aperte" },
  { id: "Chiusa", label: "Chiuse" },
];
const MSG_STATUS_TONE = { inviato: "brand", consegnato: "brand", letto: "success", fallito: "danger", aperto: "neutral", ricevuto: "warning" };
const CATEGORIES = ["Generico", "Preventivo", "Follow-up", "Appuntamento", "Assistenza", "Efficienza energetica"];

function KpiStrip({ kpi }) {
  if (!kpi) return null;
  const items = [
    ["Aperte", kpi.open_conversations], ["Da leggere", kpi.unread],
    ["Inviati oggi", kpi.sent_today], ["Ricevuti oggi", kpi.received_today], ["Follow-up", kpi.followups_todo],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="wa-kpis">
      {items.map(([l, v]) => (
        <div key={l} className="ds-card p-3"><div className="font-display text-xl font-bold text-slate-900">{v ?? 0}</div><div className="text-xs text-slate-500">{l}</div></div>
      ))}
    </div>
  );
}

export default function WhatsAppPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("chat"); // chat | templates
  const [kpi, setKpi] = useState(null);
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // conversation detail
  const [text, setText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [ctx, setCtx] = useState({ quote_id: null, appointment_id: null });
  const endRef = useRef(null);
  const waMe = kpi?.mode !== "cloud_api";

  const loadKpi = useCallback(() => { api.get("/whatsapp/dashboard").then(({ data }) => setKpi(data)).catch(() => {}); }, []);
  const loadTemplates = useCallback(() => { api.get("/whatsapp/templates").then(({ data }) => setTemplates(data)).catch(() => {}); }, []);

  const loadConvs = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (statusFilter === "unread") p.unread = true;
      else if (statusFilter) p.status = statusFilter;
      if (q) p.q = q;
      const { data } = await api.get("/whatsapp/conversations", { params: p });
      setConvs(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [statusFilter, q]);

  const openConv = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/whatsapp/conversations/${id}`);
      setActive(data);
      setParams({ conversation: id });
      if (data.unread_count > 0) { api.post(`/whatsapp/conversations/${id}/read`).then(loadConvs).catch(() => {}); }
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch { toast.error("Conversazione non accessibile"); }
  }, [setParams, loadConvs]);

  useEffect(() => { loadKpi(); loadTemplates(); }, [loadKpi, loadTemplates]);
  useEffect(() => { const t = setTimeout(loadConvs, q ? 250 : 0); return () => clearTimeout(t); }, [loadConvs]);

  // Deep-link: ?customer / ?conversation, con contesto opzionale ?quote / ?appointment / ?template
  useEffect(() => {
    const cust = params.get("customer");
    const conv = params.get("conversation");
    const quote = params.get("quote");
    const appointment = params.get("appointment");
    const tplName = params.get("template");
    setCtx({ quote_id: quote || null, appointment_id: appointment || null });
    const prefill = async () => {
      if (!tplName) return;
      try {
        const { data } = await api.post("/whatsapp/render", {
          template_name: tplName, customer_id: cust, quote_id: quote, appointment_id: appointment,
        });
        setText(data.text || "");
      } catch { /* template non trovato: si prosegue con testo libero */ }
    };
    if (cust) {
      api.post("/whatsapp/conversations/resolve", { customer_id: cust })
        .then(async ({ data }) => { await openConv(data.conversation_id); loadConvs(); await prefill(); })
        .catch(() => toast.error("Impossibile aprire la conversazione (numero mancante?)"));
    } else if (conv) {
      openConv(conv).then(prefill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (templateId = null) => {
    if (!active) return;
    if (!templateId && !text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp/send", {
        conversation_id: active.id, text: templateId ? null : text, template_id: templateId,
        quote_id: ctx.quote_id, appointment_id: ctx.appointment_id,
      });
      if (data.warning) toast.warning(data.warning);
      if (data.channel === "wa_me" && data.url) { window.open(data.url, "_blank", "noopener"); toast.success("Aperto su WhatsApp"); }
      else toast.success("Messaggio inviato");
      setText("");
      await openConv(active.id); loadConvs(); loadKpi();
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setSending(false); }
  };

  return (
    <div className="animate-fade-in-up space-y-4" data-testid="whatsapp-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><MessageCircle className="h-6 w-6 text-success" /> WhatsApp</h1>
          <p className="mt-1 text-sm text-slate-500">Centro comunicazioni. Modalità attuale: <b>{kpi?.mode === "cloud_api" ? "Cloud API" : "wa.me (fallback)"}</b>.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={view === "chat" ? "default" : "outline"} onClick={() => setView("chat")} data-testid="wa-view-chat">Conversazioni</Button>
          {isAdmin && <Button variant={view === "templates" ? "default" : "outline"} onClick={() => setView("templates")} data-testid="wa-view-templates">Template</Button>}
        </div>
      </div>

      <KpiStrip kpi={kpi} />

      {view === "templates" ? (
        <TemplatesManager templates={templates} reload={loadTemplates} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]">
          {/* Inbox */}
          <div className="ds-card flex flex-col overflow-hidden" style={{ maxHeight: "calc(100vh - 15rem)" }}>
            <div className="space-y-2 border-b border-border p-3">
              <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" placeholder="Cerca cliente, telefono, CF/P.IVA…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wa-search" /></div>
              <div className="flex flex-wrap gap-1">
                {STATUS_FILTERS.map((f) => (
                  <button key={f.id} onClick={() => setStatusFilter(f.id)} data-testid={`wa-filter-${f.id || "all"}`}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${statusFilter === f.id ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{f.label}</button>
                ))}
              </div>
            </div>
            <div className="ds-scroll flex-1 overflow-y-auto" data-testid="wa-conv-list">
              {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
                : convs.length === 0 ? <div className="py-10 text-center text-sm text-slate-500">Nessuna conversazione.</div>
                : convs.map((c) => (
                  <button key={c.id} onClick={() => openConv(c.id)} data-testid={`wa-conv-${c.id}`}
                    className={`flex w-full flex-col gap-0.5 border-b border-slate-100 px-3 py-2.5 text-left hover:bg-brand/5 ${active?.id === c.id ? "bg-brand/10" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-semibold text-slate-800">{c.customer_name || c.phone_number || "Numero non associato"}</span>
                      {c.unread_count > 0 && <span className="rounded-full bg-success px-1.5 text-[10px] font-bold text-white" data-testid={`wa-unread-${c.id}`}>{c.unread_count}</span>}
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                      <span className="truncate">{c.last_message_text || c.phone_number}</span>
                      <span className="shrink-0">{c.last_message_at ? new Date(c.last_message_at).toLocaleDateString("it-IT") : ""}</span>
                    </div>
                  </button>
                ))}
            </div>
          </div>

          {/* Chat */}
          <div className="ds-card flex flex-col overflow-hidden" style={{ height: "calc(100vh - 15rem)" }} data-testid="wa-chat">
            {!active ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 text-slate-400">
                <MessageCircle className="h-10 w-10" /><span className="text-sm">Seleziona una conversazione</span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 border-b border-border p-3">
                  <div className="min-w-0">
                    <div className="truncate font-display font-semibold text-slate-900" data-testid="wa-chat-title">{active.customer?.display_name || active.phone_number || "Numero non associato"}</div>
                    <div className="text-xs text-slate-500">{active.phone_number}</div>
                  </div>
                  <div className="flex gap-2">
                    {active.customer && <Button size="sm" variant="outline" onClick={() => navigate(`/clienti/${active.customer.id}`)} data-testid="wa-open-customer"><UserIcon className="mr-1 h-4 w-4" /> Scheda 360°</Button>}
                  </div>
                </div>
                <div className="ds-scroll flex-1 space-y-2 overflow-y-auto bg-slate-50 p-3" data-testid="wa-messages">
                  {(active.messages || []).map((m) => (
                    <div key={m.id} className={`flex ${m.direction === "outgoing" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.direction === "outgoing" ? "bg-brand/10 text-slate-800" : "bg-white text-slate-800 border border-border"}`}>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                        <div className="mt-1 flex items-center gap-2 text-[10px] text-slate-400">
                          <span>{m.created_at ? new Date(m.created_at).toLocaleString("it-IT") : ""}</span>
                          {m.direction === "outgoing" && <StatusBadge type={MSG_STATUS_TONE[m.status] || "neutral"}>{m.status}</StatusBadge>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <div className="space-y-2 border-t border-border p-3">
                  {active.customer?.whatsapp_opt_out && <div className="rounded bg-danger/10 px-2 py-1 text-xs text-danger" data-testid="wa-optout-warning">Cliente in opt-out WhatsApp: evitare comunicazioni marketing.</div>}
                  {waMe && <div className="flex items-start gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-500" data-testid="wa-wame-hint"><ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>Modalità wa.me: il pulsante <b>apre WhatsApp</b> con il messaggio già compilato. L'invio va completato manualmente da WhatsApp; il messaggio viene registrato come “aperto”, non “inviato”.</span></div>}
                  <div className="flex flex-wrap gap-2">
                    <Select onValueChange={(id) => send(id)}>
                      <SelectTrigger className="w-[220px]" data-testid="wa-template-select"><SelectValue placeholder={waMe ? "Apri WhatsApp da template…" : "Invia da template…"} /></SelectTrigger>
                      <SelectContent className="bg-white">{templates.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.category} · {t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Textarea rows={2} placeholder={waMe ? "Scrivi un messaggio (si aprirà in WhatsApp)…" : "Scrivi un messaggio…"} value={text} onChange={(e) => setText(e.target.value)} data-testid="wa-composer-input" />
                    <Button onClick={() => send()} disabled={sending || !text.trim()} data-testid="wa-send-button" title={waMe ? "Apri in WhatsApp col messaggio precompilato" : "Invia messaggio"}>{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (waMe ? <><ExternalLink className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Apri</span></> : <Send className="h-4 w-4" />)}</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplatesManager({ templates, reload }) {
  const empty = { name: "", category: "Generico", text: "", active: true };
  const [form, setForm] = useState(null);
  const save = async () => {
    try {
      if (form.id) await api.put(`/whatsapp/templates/${form.id}`, form);
      else await api.post("/whatsapp/templates", form);
      toast.success("Template salvato"); setForm(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };
  const del = async (id) => { try { await api.delete(`/whatsapp/templates/${id}`); toast.success("Eliminato"); reload(); } catch { toast.error("Errore"); } };
  return (
    <div className="ds-card p-4" data-testid="wa-templates">
      <div className="mb-3 flex items-center justify-between"><h3 className="ds-section-title">Template WhatsApp</h3>
        <Button size="sm" onClick={() => setForm({ ...empty })} data-testid="wa-tpl-new"><Plus className="mr-1 h-4 w-4" /> Nuovo</Button></div>
      {form && (
        <div className="mb-4 space-y-2 rounded-lg border border-border p-3" data-testid="wa-tpl-form">
          <div className="flex flex-wrap gap-2">
            <Input className="flex-1" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wa-tpl-name" />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="w-[200px]" data-testid="wa-tpl-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea rows={3} placeholder="Testo (variabili: {nome} {cognome} {numero_preventivo} {prodotto} {importo} {data} {ora} {operatore} {sede})" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="wa-tpl-text" />
          <div className="flex gap-2"><Button size="sm" onClick={save} data-testid="wa-tpl-save">Salva</Button><Button size="sm" variant="outline" onClick={() => setForm(null)}>Annulla</Button></div>
        </div>
      )}
      <div className="space-y-2">
        {templates.map((t) => (
          <div key={t.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3" data-testid={`wa-tpl-${t.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{t.name}</span><StatusBadge type="neutral">{t.category}</StatusBadge>{!t.active && <StatusBadge type="danger">Non attivo</StatusBadge>}</div>
              <div className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{t.text}</div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="icon" variant="ghost" onClick={() => setForm({ ...t })} data-testid={`wa-tpl-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(t.id)} data-testid={`wa-tpl-del-${t.id}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
